import { Box, Card, Tab, Tabs, Typography } from "@mui/material";
import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import DashboardFilterBar from "./DashboardFilterBar";
import ManagementTabPanel from "./components/ManagementTabPanel";
import useManagementDashboardData from "./hooks/useManagementDashboardData";
import {
  DEFAULT_MANAGEMENT_TAB,
  MANAGEMENT_DASHBOARD_TAB_ITEMS,
  MANAGEMENT_DASHBOARD_TABS,
} from "./managementDashboardConstants";
import AssetsEnergyTab from "./tabs/AssetsEnergyTab";
import ContractorIntelligenceTab from "./tabs/ContractorIntelligenceTab";
import ControlCenterTab from "./tabs/ControlCenterTab";
import ExecutiveOverviewTab from "./tabs/ExecutiveOverviewTab";
import GeographyIntelligenceTab from "./tabs/GeographyIntelligenceTab";
import LendingFinanceTab from "./tabs/LendingFinanceTab";
import OperationsControlTab from "./tabs/OperationsControlTab";

const ManagementDashboardShell = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const data = useManagementDashboardData();

  const activeTab = useMemo(() => {
    const tab = searchParams.get("tab");
    const valid = MANAGEMENT_DASHBOARD_TAB_ITEMS.some((item) => item.id === tab);
    return valid ? tab : DEFAULT_MANAGEMENT_TAB;
  }, [searchParams]);

  const handleTabChange = useCallback(
    (_event, nextTab) => {
      const next = new URLSearchParams(searchParams);
      next.set("tab", nextTab);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const tabProps = {
    ...data,
    dashboardFilters: data.dashboardFilters,
  };

  return (
    <Box sx={{ px: { xs: 1, md: 2 }, py: { xs: 1, md: 1.5 }, background: "#F6F8FB" }}>
      <Box sx={{ mb: 1.5 }}>
        <BreadcrumbsComponent />
        <Typography variant="h5" fontWeight={700} sx={{ mt: 1 }}>
          داشبورد مدیریت
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          مرکز کنترل مدیریتی — نمای اجرایی، عملیات، پیمانکاران، وام، جغرافیا و دارایی
        </Typography>
      </Box>

      <DashboardFilterBar
        loading={data.kpiLoading}
        onBootstrapReady={data.handleBootstrapReady}
      />

      <Card
        sx={{
          borderRadius: "16px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
          mb: 0,
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            px: { xs: 0.5, md: 1 },
            minHeight: 48,
            "& .MuiTab-root": { minHeight: 48, fontSize: 13, fontWeight: 600 },
          }}
        >
          {MANAGEMENT_DASHBOARD_TAB_ITEMS.map((item) => (
            <Tab
              key={item.id}
              value={item.id}
              label={item.label}
              id={`management-tab-${item.id}`}
              aria-controls={`management-tabpanel-${item.id}`}
            />
          ))}
        </Tabs>
      </Card>

      <ManagementTabPanel active={activeTab === MANAGEMENT_DASHBOARD_TABS.EXECUTIVE} id={MANAGEMENT_DASHBOARD_TABS.EXECUTIVE}>
        <ExecutiveOverviewTab {...tabProps} />
      </ManagementTabPanel>

      <ManagementTabPanel active={activeTab === MANAGEMENT_DASHBOARD_TABS.OPERATIONS} id={MANAGEMENT_DASHBOARD_TABS.OPERATIONS}>
        <OperationsControlTab {...tabProps} />
      </ManagementTabPanel>

      <ManagementTabPanel active={activeTab === MANAGEMENT_DASHBOARD_TABS.CONTRACTORS} id={MANAGEMENT_DASHBOARD_TABS.CONTRACTORS}>
        <ContractorIntelligenceTab {...tabProps} />
      </ManagementTabPanel>

      <ManagementTabPanel active={activeTab === MANAGEMENT_DASHBOARD_TABS.LENDING} id={MANAGEMENT_DASHBOARD_TABS.LENDING}>
        <LendingFinanceTab {...tabProps} />
      </ManagementTabPanel>

      <ManagementTabPanel active={activeTab === MANAGEMENT_DASHBOARD_TABS.GEOGRAPHY} id={MANAGEMENT_DASHBOARD_TABS.GEOGRAPHY}>
        <GeographyIntelligenceTab {...tabProps} />
      </ManagementTabPanel>

      <ManagementTabPanel active={activeTab === MANAGEMENT_DASHBOARD_TABS.ASSETS} id={MANAGEMENT_DASHBOARD_TABS.ASSETS}>
        <AssetsEnergyTab {...tabProps} />
      </ManagementTabPanel>

      <ManagementTabPanel active={activeTab === MANAGEMENT_DASHBOARD_TABS.CONTROL} id={MANAGEMENT_DASHBOARD_TABS.CONTROL}>
        <ControlCenterTab {...tabProps} />
      </ManagementTabPanel>
    </Box>
  );
};

export default ManagementDashboardShell;
